import { useEffect, useState } from "react";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import {
  FaxLetterApiError,
  FaxLetterDetailsResponse,
  getFaxLetterDetails,
} from "../../../api/postCall/faxLetter";

export type FaxLetterDetailsStatus =
  "loading" | "ready" | "not_found" | "error";

export interface FaxLetterDetailsState {
  status: FaxLetterDetailsStatus;
  details: FaxLetterDetailsResponse | null;
  pdfUrl: string;
  pdfLoading: boolean;
}

/**
 * Loads one fax letter's Authorization Details and resolves its PDF download URL.
 *
 * @param orderId - Order the J-code belongs to
 * @param orgId - The caller's organization
 * @param jCodeId - J-code record id
 * @param documentId - The fax letter's document id
 * @returns Load status, the details payload, and the PDF URL state
 */
export const useFaxLetterDetails = (
  orderId: string,
  orgId: string,
  jCodeId: string,
  documentId: string,
): FaxLetterDetailsState => {
  const [state, setState] = useState<FaxLetterDetailsState>({
    status: "loading",
    details: null,
    pdfUrl: "",
    pdfLoading: false,
  });

  useEffect(() => {
    let cancelled = false;
    if (!orderId || !orgId || !jCodeId || !documentId) {
      setState({
        status: "not_found",
        details: null,
        pdfUrl: "",
        pdfLoading: false,
      });
      return;
    }
    setState({
      status: "loading",
      details: null,
      pdfUrl: "",
      pdfLoading: false,
    });
    getFaxLetterDetails({
      order_id: orderId,
      org_id: orgId,
      j_code_id: jCodeId,
      document_id: documentId,
    })
      .then(async (details) => {
        if (cancelled) return;
        const path = details.document.doc_path;
        setState({ status: "ready", details, pdfUrl: "", pdfLoading: !!path });
        if (!path) return;
        const url = await getFileDownloadUrl(path);
        if (!cancelled) {
          setState((prev) => ({ ...prev, pdfUrl: url, pdfLoading: false }));
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const notFound =
          error instanceof FaxLetterApiError && error.status === 404;
        setState({
          status: notFound ? "not_found" : "error",
          details: null,
          pdfUrl: "",
          pdfLoading: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, orgId, jCodeId, documentId]);

  return state;
};
